import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ExternalLink, Loader2, Plus, RotateCcw, Trash2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { useFileUpload } from "@/hooks/use-file-upload";
import { apiDelete, apiGet, apiPut } from "@/lib/api";

export const Route = createFileRoute("/_authenticated/admin/layout")({
  component: DocumentLayoutPage,
});

const API_BASE = import.meta.env.VITE_API_URL || "";

interface Config {
  paper: "letter" | "a4" | "legal";
  margin: number;
  header: { enabled: boolean; showLogo: boolean; showChevrons: boolean; blocks: { title: string; lines: string[] }[] };
  footer: { enabled: boolean; lines: string[]; showPageNumbers: boolean };
}
interface LayoutData {
  config: Config;
  defaults: Config;
  logoFileId: string | null;
  logoName: string | null;
}

const PAPERS: { value: Config["paper"]; label: string }[] = [
  { value: "letter", label: "Letter (8.5 x 11 in)" },
  { value: "a4", label: "A4 (210 x 297 mm)" },
  { value: "legal", label: "Legal (8.5 x 14 in)" },
];

function DocumentLayoutPage() {
  const qc = useQueryClient();
  const { upload } = useFileUpload();
  const logoInput = useRef<HTMLInputElement>(null);
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "doc-layout"],
    queryFn: () => apiGet<{ data: LayoutData }>("/api/admin/doc-layout"),
  });
  const [cfg, setCfg] = useState<Config | null>(null);
  const [logoFileId, setLogoFileId] = useState<string | null | undefined>(undefined); // undefined = unchanged
  const [logoName, setLogoName] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (data?.data && !cfg) {
      setCfg(structuredClone(data.data.config));
      setLogoName(data.data.logoName);
    }
  }, [data, cfg]);

  const save = useMutation({
    mutationFn: () => apiPut("/api/admin/doc-layout", { config: cfg, ...(logoFileId !== undefined ? { logoFileId } : {}) }),
    onSuccess: () => {
      toast.success("Saved. New documents use this layout.");
      setLogoFileId(undefined);
      qc.invalidateQueries({ queryKey: ["admin", "doc-layout"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const reset = useMutation({
    mutationFn: () => apiDelete("/api/admin/doc-layout"),
    onSuccess: () => {
      toast.success("Back to the standard layout");
      setCfg(null);
      setLogoFileId(undefined);
      qc.invalidateQueries({ queryKey: ["admin", "doc-layout"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  async function pickLogo(file: File | undefined) {
    if (!file) return;
    if (!/^image\/(png|jpeg)$/.test(file.type)) return toast.error("The logo must be a PNG or JPG picture");
    setUploading(true);
    try {
      const { fileId } = await upload(file, { fileName: file.name, mimeType: file.type, purpose: "photo", skipCompression: true });
      setLogoFileId(fileId);
      setLogoName(file.name);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not upload the logo");
    } finally {
      setUploading(false);
    }
  }

  async function preview() {
    try {
      // Save first so the preview shows what is on screen
      await apiPut("/api/admin/doc-layout", { config: cfg, ...(logoFileId !== undefined ? { logoFileId } : {}) });
      setLogoFileId(undefined);
      qc.invalidateQueries({ queryKey: ["admin", "doc-layout"] });
      const res = await fetch(`${API_BASE}/api/admin/doc-layout/preview`, { credentials: "include" });
      if (!res.ok) throw new Error("Could not build the preview");
      const url = URL.createObjectURL(await res.blob());
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not build the preview");
    }
  }

  if (isLoading || (!cfg && !error)) return <Skeleton className="h-64 w-full rounded-xl" />;
  if (error || !cfg) return <p className="text-sm text-destructive">{(error as Error | null)?.message ?? "Could not open the settings"}</p>;

  const setHeader = (patch: Partial<Config["header"]>) => setCfg({ ...cfg, header: { ...cfg.header, ...patch } });
  const setFooter = (patch: Partial<Config["footer"]>) => setCfg({ ...cfg, footer: { ...cfg.footer, ...patch } });
  const setBlock = (i: number, patch: Partial<{ title: string; lines: string[] }>) =>
    setHeader({ blocks: cfg.header.blocks.map((b, k) => (k === i ? { ...b, ...patch } : b)) });
  const check = (id: string, label: string, checked: boolean, onChange: (v: boolean) => void) => (
    <label htmlFor={id} className="flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 text-sm">
      <input id={id} type="checkbox" className="h-4 w-4" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );

  return (
    <div className="mx-auto max-w-2xl space-y-5 pb-24">
      <div>
        <h1 className="text-xl font-semibold">Document layout</h1>
        <p className="text-sm text-muted-foreground">
          The header, footer, paper size and margins of every exported PDF: toolbox meetings, FLHAs and reports. Changes apply to documents made from now on.
        </p>
      </div>

      <Card>
        <CardContent className="space-y-3 p-4">
          <p className="text-sm font-semibold">Page</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="dl-paper">Paper size</Label>
              <select id="dl-paper" value={cfg.paper} onChange={(e) => setCfg({ ...cfg, paper: e.target.value as Config["paper"] })} className="h-11 w-full rounded-md border bg-background px-3 text-base md:text-sm">
                {PAPERS.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="dl-margin">Margins (points; 72 = 1 inch)</Label>
              <Input id="dl-margin" type="number" min={24} max={72} value={cfg.margin} onChange={(e) => setCfg({ ...cfg, margin: Number(e.target.value) })} className="h-11 text-base md:text-sm" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-3 p-4">
          <p className="text-sm font-semibold">Header (top of every page)</p>
          {check("dl-h-on", "Show the header", cfg.header.enabled, (v) => setHeader({ enabled: v }))}
          {cfg.header.enabled && (
            <>
              {check("dl-h-logo", "Show the logo", cfg.header.showLogo, (v) => setHeader({ showLogo: v }))}
              {cfg.header.showLogo && (
                <div className="flex flex-wrap items-center gap-2 rounded-lg border p-3 text-sm">
                  <span className="min-w-0 flex-1 truncate">{logoFileId === null ? "Standard logo" : logoName ?? "Standard logo"}</span>
                  <Button type="button" size="sm" variant="outline" className="gap-1" disabled={uploading} onClick={() => logoInput.current?.click()}>
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload a logo
                  </Button>
                  {(logoName || logoFileId) && logoFileId !== null && (
                    <Button type="button" size="sm" variant="ghost" onClick={() => { setLogoFileId(null); setLogoName(null); }}>Use the standard logo</Button>
                  )}
                  <input ref={logoInput} type="file" accept="image/png,image/jpeg" className="hidden" aria-label="Choose a logo" onChange={(e) => { void pickLogo(e.target.files?.[0]); e.target.value = ""; }} />
                </div>
              )}
              {check("dl-h-chev", "Show the chevrons on the right", cfg.header.showChevrons, (v) => setHeader({ showChevrons: v }))}

              <p className="pt-1 text-xs font-medium text-muted-foreground">Address blocks (up to 3, shown side by side)</p>
              {cfg.header.blocks.map((b, i) => (
                <div key={i} className="space-y-2 rounded-lg border p-3">
                  <div className="flex items-center gap-2">
                    <Input value={b.title} onChange={(e) => setBlock(i, { title: e.target.value })} placeholder="Title, e.g. Custom Homes Div." aria-label={`Block ${i + 1} title`} className="h-10 text-base md:text-sm" />
                    <Button type="button" size="icon" variant="ghost" aria-label={`Remove block ${i + 1}`} onClick={() => setHeader({ blocks: cfg.header.blocks.filter((_, k) => k !== i) })}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <Textarea value={b.lines.join("\n")} onChange={(e) => setBlock(i, { lines: e.target.value.split("\n").slice(0, 4) })} rows={3} placeholder={"One line each:\n390 Harbour Ave, North Vancouver\nBritish Columbia V7J 2E9"} aria-label={`Block ${i + 1} lines`} />
                </div>
              ))}
              {cfg.header.blocks.length < 3 && (
                <Button type="button" size="sm" variant="outline" className="gap-1" onClick={() => setHeader({ blocks: [...cfg.header.blocks, { title: "", lines: [] }] })}>
                  <Plus className="h-4 w-4" /> Add a block
                </Button>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-3 p-4">
          <p className="text-sm font-semibold">Footer (bottom of every page)</p>
          {check("dl-f-on", "Show the footer", cfg.footer.enabled, (v) => setFooter({ enabled: v }))}
          {cfg.footer.enabled && (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="dl-f-lines">Footer lines (up to 3, centred)</Label>
                <Textarea id="dl-f-lines" value={cfg.footer.lines.join("\n")} onChange={(e) => setFooter({ lines: e.target.value.split("\n").slice(0, 3) })} rows={3} placeholder={"e.g. Paris Plumbing and Heating Ltd., operating as Paris Mechanical"} />
              </div>
              {check("dl-f-num", "Show page numbers", cfg.footer.showPageNumbers, (v) => setFooter({ showPageNumbers: v }))}
            </>
          )}
        </CardContent>
      </Card>

      <div className="fixed inset-x-0 bottom-14 z-30 border-t bg-background/95 px-4 py-3 backdrop-blur md:bottom-0 md:left-60">
        <div className="mx-auto flex max-w-2xl flex-wrap gap-2">
          <Button type="button" disabled={save.isPending} onClick={() => save.mutate()}>{save.isPending ? "Saving..." : "Save"}</Button>
          <Button type="button" variant="outline" className="gap-1" onClick={preview}><ExternalLink className="h-4 w-4" /> Save and preview PDF</Button>
          <Button
            type="button"
            variant="ghost"
            className="ml-auto gap-1 text-muted-foreground"
            disabled={reset.isPending}
            onClick={() => window.confirm("Go back to the standard layout? Your header, footer and logo settings are removed.") && reset.mutate()}
          >
            <RotateCcw className="h-4 w-4" /> Standard layout
          </Button>
        </div>
      </div>
    </div>
  );
}
